import { useGyymApiGlobalErrorHandler } from '@gyym/sdk-react'
import { useScroll } from '@gyym/ui'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'

import { GTMScript } from '@/components/Scripts/GoogleTagManager'
import { identify } from '@/lib/googleTagManager'
import { useAuthUser } from '@/modules/Auth'

import { Footer } from './Footer'
import { Header } from './Header'
import { SidebarContainer } from './SidebarContainer'
import { Container, Content } from './styles'
import { useAnalyticsTrackingEffect } from './useAnalytics'
import { usePageView } from './usePageView'
import { useShouldChangePasswordEffect } from './useShouldChangePasswordEffect'

export { HEADER_HEIGHT } from './Header'

const STICKY_HEADER_OFFSET = 300

export interface BaseLayoutProps {
  title: string
  description: string
  keywords: string[]
  ogImageUrl?: string
  isTop?: boolean
  isStickyHeader?: boolean
  hideTabletFooter?: boolean
  children: React.ReactNode
}

export const BaseLayout = ({
  title,
  description,
  keywords,
  ogImageUrl,
  isTop,
  isStickyHeader = true,
  hideTabletFooter = false,
  children,
}: BaseLayoutProps) => {
  const router = useRouter()
  const { isLoggedIn, authUser } = useAuthUser()
  const [isIdentifySubmitted, setIsIdentifySubmitted] = React.useState(false)

  usePageView()

  useEffect(() => {
    if (authUser && !isIdentifySubmitted) {
      setIsIdentifySubmitted(true)
      // Identify user
      identify(authUser)
    }
  }, [authUser, isIdentifySubmitted])

  useGyymApiGlobalErrorHandler(
    (e) => {
      if ((e.status === 401 || e.status === 419) && isLoggedIn) {
        // If the login state differs from server side, redirect to login page
        router.push('/login')
      } else {
        // Else reject the promise
        return Promise.reject(e)
      }
    },
    [isLoggedIn, router],
  )

  const [sidebarOpen, setSidebarOpen] = React.useState(false)
  const handleSidebarOpen = React.useCallback(() => setSidebarOpen(true), [])
  const handleSidebarClose = React.useCallback(() => setSidebarOpen(false), [])

  const { y } = useScroll()
  const isSticky = !isTop ? isStickyHeader : STICKY_HEADER_OFFSET < y

  // Show dialog if user should change password
  useShouldChangePasswordEffect()

  // Track analytics campaign
  useAnalyticsTrackingEffect()

  return (
    <Container>
      <Head>
        <title key="title">{title}</title>
        <meta key="og:title" property="og:title" content={title} />
        <meta key="description" name="description" content={description} />
        <meta
          key="og:description"
          property="og:description"
          content={description}
        />
        <meta key="keywords" name="keywords" content={keywords.join(',')} />
        {ogImageUrl && (
          <meta key="og:image" property="og:image" content={ogImageUrl} />
        )}
        <meta key="og:site_name" property="og:site_name" content="GYYM" />
        <meta key="fb:app_id" property="fb:app_id" content="119028739546685" />
        <meta
          key="twitter:card"
          name="twitter:card"
          content="summary_large_image"
        />
        <meta key="og:type" property="og:type" content="website" />
        <meta key="og:url" property="og:url" content="https://gyym.jp" />
      </Head>

      <GTMScript />

      <Header
        isSticky={isSticky}
        isTop={isTop}
        onMenuClick={handleSidebarOpen}
      />

      <Content
        topPadded={isSticky && !isTop} // Header height
      >
        {children}
      </Content>

      <SidebarContainer open={sidebarOpen} onClose={handleSidebarClose} />

      <Footer hideTabletFooter={hideTabletFooter} />
    </Container>
  )
}
